import { Fragment, useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { acars, live as liveApi, debriefs } from "@/lib/api";
import DebriefCard from "@/components/DebriefCard";
import { TileLayer, Marker, Polyline, useMap, useMapEvents, CircleMarker } from "react-leaflet";
import { tileUrlFor, MAP_ATTRIBUTION, MAP_SUBDOMAINS } from "../lib/basemap";
import { useTheme } from "next-themes";
import SafeMap from "@/components/StrictModeSafeMap";
import L from "leaflet";
import { fmtZulu, phaseTone } from "@/lib/format";
import { useFlightSession } from "@/lib/flight-session";
import { PlaneTakeoff, PlaneLanding, Radio, Loader2, X, Send, Wifi, WifiOff, RefreshCw } from "lucide-react";
import { toast } from "sonner";

/**
 * Name the simulators the desktop app is actually probing.
 *
 * This used to be the literal string "Waiting for MSFS / FSUIPC…", which on
 * macOS and Linux names the two adapters that cannot possibly answer there —
 * SimConnect is MSFS-only and FSUIPC7 is a Windows application. An X-Plane
 * pilot was told the app was waiting for something they do not own.
 *
 * `candidates` comes from the connector's own probe order, so this cannot
 * drift from what the app is doing. The fallback covers an older desktop
 * build that does not send the field yet.
 */
const SIM_LABELS = { simconnect: "MSFS", fsuipc: "FSUIPC", xplane: "X-Plane" };
function simNames(candidates) {
  const names = (candidates || []).map((c) => SIM_LABELS[c] || c);
  return names.length ? names.join(" / ") : "a simulator";
}
import { useElectronSim } from "@/lib/electron";
import { airportLatLon, greatCircleArc, splitRouteAt } from "@/lib/airports";
import { NasAdvisoriesCard } from "@/components/NasAdvisoriesCard";
import TelemetryNotice from "@/components/TelemetryNotice";
import { escapeHtml } from "@/lib/escape-html";

// Plane silhouette — SVG top-down view. Native orientation is "up" (0°),
// so rotate by the raw heading (no offset).
const JET_SVG = `<path d="M12 2 L13 9 L22 12 L22 14 L13 13 L13 19 L15 20 L15 21 L9 21 L9 20 L11 19 L11 13 L2 14 L2 12 L11 9 Z" fill="currentColor"/>`;

/** Heading as 0-359, or null when the sim never sent one. See LiveMap.jsx:
 *  `Number(active.hdg) || 0` drew a plane pointing due north for a flight with
 *  no heading at all, which is the same picture as a flight tracking 360. */
export function normHdg(v) {
  const n = Number(v);
  if (v === null || v === undefined || v === "" || !Number.isFinite(n)) return null;
  return ((n % 360) + 360) % 360;
}

const planeIcon = (hdg = null) =>
  L.divIcon({
    className: "vac-plane-icon",
    html: hdg === null
      ? `<div style="width:28px;height:28px;display:flex;align-items:center;justify-content:center;filter:drop-shadow(0 0 4px var(--map-halo))">
           <div style="width:16px;height:16px;border-radius:50%;border:2px solid #F97316;box-sizing:border-box"></div>
         </div>`
      : `<div style="width:28px;height:28px;transform:rotate(${hdg}deg);color:#F97316;filter:drop-shadow(0 0 4px var(--map-halo))">
           <svg viewBox="0 0 24 24" width="28" height="28">${JET_SVG}</svg>
         </div>`,
    iconSize: [28, 28],
    iconAnchor: [14, 14],
  });

/**
 * One line of VATSIM coverage for the two ends of this flight.
 *
 * Deliberately terse: this sits under the route on a page a pilot glances at
 * mid-flight, not a briefing they read on the ground. The ATIS LETTER is the
 * payload — a crew watching it change from C to D knows the runway or the
 * numbers moved and they need the full text, which is on the Flight Plan page.
 *
 * Silent when the feed cannot be read. On the briefing page an unreachable
 * feed is worth saying out loud because the pilot is making decisions from it;
 * here it would be a permanent grey line on the flight display saying nothing
 * useful.
 */
function AtcStrip({ atc, orig, dest }) {
  if (!atc || atc.feed === "unavailable") return null;
  const end = (icao) => {
    const e = atc.airports?.[icao];
    if (!e) return null;
    const twr = e.positions?.[0];
    const atis = e.atis_all?.[0] || e.atis;
    return { icao, twr, atis };
  };
  const ends = [end(orig), end(dest)].filter(Boolean);
  if (!ends.length) return null;

  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-[11px] font-mono"
         data-testid="tracking-atc">
      {ends.map(({ icao, twr, atis }) => (
        <span key={icao} className="flex items-center gap-1.5">
          <span className="text-muted-foreground">{icao}</span>
          {twr && (
            <span className="text-primary">{twr.facility} {twr.frequency}</span>
          )}
          {atis && (
            <span className="px-1.5 rounded-sm border border-primary/40 bg-primary/10 text-primary">
              ATIS {atis.code || "—"}
            </span>
          )}
          {!twr && !atis && <span className="text-muted-foreground/60">uncontrolled</span>}
        </span>
      ))}
    </div>
  );
}

// inline-block + nowrap, as on the Live Map: the marker is zero wide, so a
// block box was only its padding wide and the code ran out past it, light grey
// on the day map's light tiles. The box now fits the code.
const aptLabelIcon = (code) =>
  L.divIcon({
    className: "vac-apt-label",
    html: `<div style="display:inline-block;white-space:nowrap;font:600 10px/1 'JetBrains Mono',monospace;color:#e8eaed;background:rgba(11,15,20,0.85);padding:2px 5px;border-radius:2px;letter-spacing:0.05em">${escapeHtml(code)}</div>`,
    iconSize: [0, 0],
    iconAnchor: [-6, 6],
  });

/** Keeps the aircraft in view, with a smooth pan as it moves, until the pilot
 *  drags the map to look around; Re-centre (in the page) brings it back. It
 *  used to setView on every position, about every 3 s, so the map snapped back
 *  whenever a pilot moved it (beta report 2026-10-20: "the map moving me
 *  around"). Zooming does not stop following. */
function FollowAircraft({ lat, lon, follow, onPilotMoved }) {
  const map = useMap();
  useMapEvents({ dragstart: () => onPilotMoved() });
  useEffect(() => {
    if (follow && lat && lon) map.panTo([lat, lon], { animate: true, duration: 1, easeLinearity: 1 });
  }, [lat, lon, follow, map]);
  return null;
}

/** Once a flight has filed, its debrief (made a moment after filing). Asked
 *  for fresh on opening and every 15 s while open, so the flight just filed
 *  replaces an earlier one; nothing if the last is over 12 hours old. */
function LastFlightDebrief() {
  const { data } = useQuery({
    queryKey: ["debrief-latest"],
    queryFn: debriefs.latest,
    staleTime: 0,
    refetchOnMount: "always",
    refetchInterval: 15_000,
  });
  if (!data?.token) return null;
  return (
    <section className="mb-6" data-testid="tracking-last-debrief">
      <div className="overline mb-1">Your last flight</div>
      <h2 className="text-xl font-bold mb-3">Flight debrief</h2>
      <DebriefCard debrief={data} />
    </section>
  );
}

export default function LiveTracking() {
  const { resolvedTheme } = useTheme();
  const nav = useNavigate();
  const qc = useQueryClient();
  const [confirmingCancel, setConfirmingCancel] = useState(false);
  const [follow, setFollow] = useState(true);   // the map keeps the aircraft in view

  const { data: active, isLoading } = useQuery({
    queryKey: ["acars-active"],
    queryFn: acars.active,
    refetchInterval: 2000,
  });

  // Electron desktop shell — sim status + telemetry stream
  const sim = useElectronSim(active);

  // Destination ATC and ATIS. The ATIS letter changing mid-flight is exactly
  // what a real crew watches for on the way in, and it costs no extra request
  // to VATSIM — the same cached datafeed the presence badges read. 60 s is the
  // interval the feed itself asks for; polling faster would only re-read cache.
  const { data: atc } = useQuery({
    queryKey: ["vatsim-atc", active?.orig, active?.dest],
    queryFn: () => liveApi.atc([active?.orig, active?.dest]),
    enabled: !!(active?.dest || active?.orig),
    refetchInterval: 60_000,
    staleTime: 55_000,
  });

  // Phase, event log and route history are recorded app-wide by
  // FlightRecorder (lib/flight-session.js). They used to be this page's own
  // state, so every visit started them again: the log re-stamped with the time
  // of the visit and the route drawn so far was gone (reported 2026-10-04).
  const session = useFlightSession();
  const mine = !!active && String(session.id) === String(active.id);
  const trail = mine ? session.trail : [];
  const events = mine ? session.events : [];

  const cancel = useMutation({
    mutationFn: () => acars.cancel(active.id),
    onSuccess: () => {
      qc.invalidateQueries();
      setConfirmingCancel(false);
      toast.info("Flight cancelled.");
      nav("/");
    },
    onError: (e) => {
      setConfirmingCancel(false);
      toast.error(e.response?.data?.detail || e.message || "Cancel failed");
    },
  });

  // Seeded from the assignment, overridable by the pilot.
  //
  // The figure is decided once, at bid time, by myvac's choosefin(): a load
  // factor against the type's seat count, forced to 0 for the cargo and ferry
  // flight-number ranges. The assignment row is the only copy, and the server
  // reads the same row when the PIREP is filed — so seeding the box means the
  // pilot sees the number that is actually going to be recorded, instead of an
  // empty field and a placeholder.
  //
  // Still a string, not a number: "" (nobody said) and "0" (flew empty) are
  // different answers, and the second is a real one on a ferry leg.
  const [paxInput, setPaxInput] = useState("");
  const [paxTouched, setPaxTouched] = useState(false);

  // Explicit null/undefined checks, never `plannedPax || ""` — an assigned 0
  // is an answer (ferry and cargo legs carry nobody), not a missing value.
  //
  // Keyed on the flight id, not on `active`, so a poll that briefly has no row
  // cannot wipe a figure the pilot has not touched. Once they type, the box is
  // theirs: clearing it back to empty is them declining to claim a number.
  const plannedPax = active?.planned_pax;
  const activeId = active?.id;
  useEffect(() => {
    if (paxTouched || !activeId) return;
    setPaxInput(plannedPax === null || plannedPax === undefined ? "" : String(plannedPax));
  }, [activeId, plannedPax, paxTouched]);

  // Filing before touchdown files a flight that never arrived. The server
  // takes OFF and ON from the live row `or now` (server.py acars_pirep), so an
  // early submit does not fail — it writes a logbook row whose OFF and ON are
  // both the moment the pilot pressed the button, and a block time to match.
  // Nothing downstream can tell that apart from a real flight.
  //
  // ON is the line: once the wheels are down the flight happened, and the only
  // thing still outstanding is the taxi in, which auto-file completes.
  //
  // Deliberately NOT enforced server-side. A missed ON already strands flights
  // (SYSTEM-GUIDE §7, "needs guaranteed frames"), and a server that refused
  // would leave that pilot with no way to file at all. The escape hatch is the
  // portal's manual PIREP, which is where manual filing lives.
  const landed = !!Number(active?.time_on || 0);

  const filePirep = useMutation({
    mutationFn: () => acars.pirep(active.id, {
      pax: paxInput.trim() === "" ? null : Number(paxInput),
      remarks: null,
    }),
    onSuccess: (r) => {
      qc.invalidateQueries();
      toast.success("PIREP submitted to FlyVAC 📡");
      nav("/logbook");
    },
    onError: (e) => toast.error(e.response?.data?.detail || "PIREP failed"),
  });

  const phase = (mine && session.phase) || "—";

  if (isLoading) {
    return <div className="p-8 text-muted-foreground">Loading…</div>;
  }

  if (!active) {
    return (
      <div className="p-8 max-w-4xl" data-testid="tracking-empty">
        <LastFlightDebrief />
        <div className="card-flat p-10 text-center">
          <Radio className="w-8 h-8 mx-auto mb-3 text-muted-foreground" />
          <div className="text-lg font-semibold mb-1">No active flight</div>
          <p className="text-sm text-muted-foreground">
            Initialize a flight to start live tracking.
          </p>
        </div>
      </div>
    );
  }

  const lat = Number(active.pos_lat) || 0;
  const lon = Number(active.pos_long) || 0;
  const hasPos = lat !== 0 || lon !== 0;

  return (
    <div className="h-full grid grid-cols-1 lg:grid-cols-12" data-testid="tracking-page">
      {/* MAP */}
      <div className="relative lg:col-span-8 h-[60vh] lg:h-auto border-b lg:border-b-0 lg:border-r border-border">
        {hasPos && !follow && (
          <button type="button" onClick={() => setFollow(true)} data-testid="tracking-recenter"
            className="absolute top-3 right-3 z-[1000] text-xs font-bold uppercase tracking-widest px-3 py-2 rounded-md border border-border bg-card/95 hover:bg-secondary shadow">
            Re-centre
          </button>
        )}
        <SafeMap
          center={hasPos ? [lat, lon] : [45, -80]}
          zoom={hasPos ? 6 : 4}
          style={{ height: "100%", width: "100%" }}
          scrollWheelZoom
        >
          <TileLayer
            attribution={MAP_ATTRIBUTION}
            key={resolvedTheme}
            url={tileUrlFor(resolvedTheme)}
            subdomains={MAP_SUBDOMAINS}
          />
          {hasPos && (
            <>
              <Marker position={[lat, lon]} icon={planeIcon(normHdg(active.hdg))} />
              <FollowAircraft lat={lat} lon={lon} follow={follow} onPilotMoved={() => setFollow(false)} />
            </>
          )}
          {/* The planned route. `active.route` is the SimBrief navlog, snapshotted
              server-side at init (migration 008); when the pilot has not pinned
              an OFP to the bid there is no route to draw and this falls back to
              the dep → arr great-circle, drawn dotted so the estimate does not
              masquerade as a filed route. Airport positions come from the
              server (orig_pos/dest_pos), the bundled list only as a fallback.
              It used to need BOTH airports in that list and drew nothing at all
              otherwise: no dots, no route, even with a filed navlog. Now each
              piece draws when what it needs is known. */}
          {(() => {
            const dep = airportLatLon(active.orig, active.orig_pos);
            const arr = airportLatLon(active.dest, active.dest_pos);
            const cur = hasPos ? [lat, lon] : null;
            const planned = Array.isArray(active.route) && active.route.length > 1
              ? active.route : null;
            let flown = [], ahead = [];
            if (planned) {
              ({ flown, ahead } = splitRouteAt(planned, cur));
            } else {
              if (dep && cur) flown = greatCircleArc(dep, cur);
              if (arr) ahead = greatCircleArc(cur || dep || arr, arr);
            }
            const dash = planned ? "4 5" : "1 4";
            return (
              <>
                {flown.length > 1 && (
                  <Polyline
                    positions={flown}
                    pathOptions={{ color: "#e31937", weight: 2, opacity: 0.75, dashArray: dash }}
                  />
                )}
                {ahead.length > 1 && (
                  <Polyline
                    positions={ahead}
                    pathOptions={{ color: "#8a9098", weight: 1.5, opacity: 0.6, dashArray: dash }}
                  />
                )}
                {[[dep, active.orig], [arr, active.dest]].map(([pos, code]) => pos && (
                  <Fragment key={code}>
                    <CircleMarker
                      center={pos}
                      radius={3}
                      pathOptions={{ color: "#fff", fillColor: "#fff", fillOpacity: 1, weight: 1 }}
                    />
                    <Marker position={pos} icon={aptLabelIcon(code)} interactive={false} />
                  </Fragment>
                ))}
              </>
            );
          })()}
          {trail.length > 1 && (
            <Polyline positions={trail} pathOptions={{ color: "#F97316", weight: 2, opacity: 0.85 }} />
          )}
        </SafeMap>
        <style>{`
          .vac-plane-icon, .vac-apt-label { background: none !important; border: none !important; }
        `}</style>
      </div>

      {/* TELEMETRY PANEL */}
      <aside className="lg:col-span-4 bg-card overflow-y-auto" data-testid="tracking-telemetry">
        <div className="px-5 pt-5 space-y-3">
          <TelemetryNotice active={active} />
          <NasAdvisoriesCard hasActive={!!active?.id} />
        </div>
        {active.type_mismatch && (
          <div
            className="px-5 py-3 border-b border-red-500/40 bg-red-500/10 text-red-300 text-xs leading-relaxed"
            data-testid="type-mismatch-warning"
          >
            <div className="font-mono uppercase tracking-wider text-red-400 mb-1">
              ⚠ Aircraft Type Mismatch
            </div>
            <div>
              Selected: <span className="font-mono">{active.type_mismatch.selected}</span>
            </div>
            <div className="truncate">
              Detected: <span className="font-mono">{active.type_mismatch.detected}</span>
            </div>
            <div className="mt-1 opacity-80">
              Load the correct aircraft or change your flight type before continuing.
            </div>
          </div>
        )}
        <div className="p-5 border-b border-border">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <span className="live-dot" />
              <span className="overline">Live</span>
            </div>
            <span
              className={`phase-pill ${phaseTone(phase)}`}
              data-testid="tracking-phase"
            >
              {phase}
            </span>
          </div>
          <div className="flex items-baseline gap-3">
            <div className="text-2xl font-semibold telemetry-value">{active.flt}</div>
            <div className="text-lg font-mono">
              {active.orig}
              <span className="mx-2 text-muted-foreground">→</span>
              {active.dest}
            </div>
          </div>
          <div className="text-xs text-muted-foreground font-mono mt-1">
            {active.type} · {active.pid}
          </div>
          <AtcStrip atc={atc} orig={active.orig} dest={active.dest} />
        </div>

        {/* Sim connection status (desktop shell only) */}
        {sim.desktop && (
          <div
            className={`px-5 py-3 border-b border-border flex items-center gap-3 ${
              sim.status.connected ? "bg-primary/5" : "bg-secondary"
            }`}
            data-testid="tracking-sim-status"
          >
            {sim.status.connected ? (
              <Wifi className="w-4 h-4 text-primary shrink-0" />
            ) : (
              <WifiOff className="w-4 h-4 text-muted-foreground shrink-0" />
            )}
            <div className="min-w-0 flex-1">
              <div className="overline">Sim link</div>
              <div className="text-xs font-mono">
                {sim.status.connected
                  ? <>Connected · <span className="text-primary uppercase">{sim.status.sim}</span></>
                  : `Waiting for ${simNames(sim.status.candidates)}…`}
              </div>
            </div>
            {window.electronAPI?.sim?.reconnect && (
              <button
                type="button"
                onClick={() => {
                  window.electronAPI.sim.reconnect();
                  toast.info("Looking for the simulator again…");
                }}
                title="Drop the sim link and look for the simulator again. Use this if it is stuck after the sim crashed or restarted."
                data-testid="tracking-sim-reconnect"
                className="text-[11px] bg-secondary rounded-md px-2.5 py-1.5 hover:bg-primary hover:text-primary-foreground flex items-center gap-1 font-mono uppercase tracking-widest shrink-0"
              >
                <RefreshCw className="w-3 h-3" />
                Reconnect
              </button>
            )}
          </div>
        )}

        {/* Big numbers */}
        <div className="grid grid-cols-2 gap-0 border-b border-border">
          <Tile label="Altitude" value={active.alt} unit="ft" />
          <Tile label="Ground Speed" value={active.spd} unit="kt" bordered="left" />
          <Tile label="Heading" value={normHdg(active.hdg) === null ? "—" : Math.round(normHdg(active.hdg))} unit="°" bordered="top" />
          <Tile label="Vertical Speed" value={active.vs} unit="fpm" bordered="left top" />
          <Tile label="IAS" value={active.spdI} unit="kt" bordered="top" />
          <Tile label="Fuel" value={active.fuel} unit="kg" bordered="left top" />
        </div>

        {/* OOOI times */}
        <div className="p-5 border-b border-border">
          <div className="overline mb-3">OOOI times</div>
          <div className="grid grid-cols-4 gap-2 text-center">
            <TimeCell label="OUT" v={active.time_out} />
            <TimeCell label="OFF" v={active.time_off} />
            <TimeCell label="ON"  v={active.time_on} />
            <TimeCell label="IN"  v={active.time_in} />
          </div>
          {/* IN is now the pilot's signal, as in VACARS3, so say what it is
              once there is a landing to finish. */}
          {landed && !Number(active.time_in || 0) && (
            <div className="mt-3 text-xs text-muted-foreground" data-testid="tracking-in-hint">
              At the gate, set the <b className="text-foreground">parking brake</b>, shut
              down the <b className="text-foreground">engines</b> and open the{" "}
              <b className="text-foreground">door</b> to log IN and file the flight.
            </div>
          )}
          {active.ldg_vs != null && Number(active.ldg_vs) !== 0 && (
            <div className="mt-4 flex items-center justify-between border-t border-border pt-3">
              <span className="overline">Landing rate</span>
              <span className="telemetry-value text-lg" data-testid="tracking-ldg-vs">
                {Number(active.ldg_vs).toFixed(0)} fpm
              </span>
            </div>
          )}
        </div>

        {/* Event log */}
        <div className="p-5 border-b border-border">
          <div className="overline mb-3">Event log</div>
          {events.length === 0 ? (
            <div className="text-xs text-muted-foreground">Awaiting bridge telemetry…</div>
          ) : (
            <ul className="space-y-1.5 max-h-52 overflow-y-auto text-xs font-mono">
              {events.map((e, i) => (
                <li key={i} className="flex justify-between text-muted-foreground">
                  <span>{e.msg}</span>
                  <span className="opacity-60">{new Date(e.ts).toISOString().slice(11, 19)}Z</span>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Actions */}
        <div className="p-5 space-y-2">
          <label className="block" hidden={!landed}>
            <span className="overline block mb-1.5">Passengers</span>
            <input
              type="number"
              min="0"
              max="9999"
              value={paxInput}
              onChange={(e) => { setPaxTouched(true); setPaxInput(e.target.value); }}
              placeholder={plannedPax === null || plannedPax === undefined
                ? "not in your assignment" : "from your assignment"}
              data-testid="tracking-input-pax"
              className="w-full bg-background border border-border px-3 py-2 text-sm font-mono focus:outline-none focus:border-primary transition-colors"
            />
          </label>
          <button
            onClick={() => filePirep.mutate()}
            disabled={filePirep.isPending || !landed}
            data-testid="tracking-submit-pirep"
            title={landed ? undefined : "Available once the aircraft has landed"}
            className="w-full bg-primary text-primary-foreground text-sm font-semibold rounded py-2.5 hover:bg-primary/90 active:scale-[0.98] transition-all disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {filePirep.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            Submit PIREP
          </button>
          {!landed && (
            <p className="text-[11px] text-muted-foreground leading-relaxed" data-testid="tracking-pirep-locked">
              Available once you have landed. Filing before touchdown would
              record your takeoff and landing as the moment you pressed the
              button. If a flight ends without ACARS seeing the landing, file it
              manually on the crew portal instead.
            </p>
          )}
          {confirmingCancel ? (
            <div className="border border-destructive/60 rounded p-2 space-y-2" data-testid="tracking-cancel-confirm">
              <div className="text-xs text-destructive text-center">
                Cancel this ACARS session? Data will be discarded.
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setConfirmingCancel(false)}
                  data-testid="tracking-cancel-abort"
                  className="flex-1 text-xs border border-border rounded py-1.5 text-muted-foreground hover:text-foreground"
                >
                  Keep flight
                </button>
                <button
                  onClick={() => cancel.mutate()}
                  disabled={cancel.isPending}
                  data-testid="tracking-cancel-confirm-btn"
                  className="flex-1 bg-destructive text-destructive-foreground text-xs font-bold uppercase tracking-widest rounded py-1.5 hover:brightness-110 disabled:opacity-60 flex items-center justify-center gap-1"
                >
                  {cancel.isPending ? <Loader2 className="w-3 h-3 animate-spin" /> : <X className="w-3 h-3" />}
                  Confirm cancel
                </button>
              </div>
            </div>
          ) : (
            <button
              onClick={() => setConfirmingCancel(true)}
              disabled={cancel.isPending}
              data-testid="tracking-cancel"
              className="w-full border border-destructive/40 text-destructive text-xs rounded py-2 hover:bg-destructive/10 transition-colors flex items-center justify-center gap-2"
            >
              <X className="w-3.5 h-3.5" />
              Cancel flight
            </button>
          )}
        </div>
      </aside>
    </div>
  );
}

function Tile({ label, value, unit, bordered = "" }) {
  const cls = [
    "p-5",
    bordered.includes("left") ? "border-l border-border" : "",
    bordered.includes("top") ? "border-t border-border" : "",
  ].join(" ");
  return (
    <div className={cls}>
      <div className="overline mb-2">{label}</div>
      <div className="flex items-baseline gap-1.5">
        <span className="telemetry-value text-3xl">{value ?? 0}</span>
        <span className="text-xs text-muted-foreground font-mono">{unit}</span>
      </div>
    </div>
  );
}

function TimeCell({ label, v }) {
  return (
    <div>
      <div className="overline mb-1">{label}</div>
      <div className="telemetry-value text-sm">{v ? fmtZulu(v) : "—"}</div>
    </div>
  );
}
